import { useEffect, useState } from "react";

/**
 * Feature 3 - Blur When Window Loses Focus.
 *
 * Reports whether the browser window currently has focus. When `enabled` is
 * false it reports focused = true (never blurs).
 *
 * @param {object} opts
 * @param {boolean} opts.enabled
 * @param {(focused: boolean) => void} [opts.onChange]
 */
export function useWindowFocus({ enabled, onChange } = {}) {
  const [isFocused, setIsFocused] = useState(true);

  useEffect(() => {
    if (!enabled) {
      setIsFocused(true);
      return undefined;
    }

    const set = (focused) => {
      setIsFocused(focused);
      if (typeof onChange === "function") onChange(focused);
    };
    const handleBlur = () => set(false);
    const handleFocus = () => set(true);

    window.addEventListener("blur", handleBlur, false);
    window.addEventListener("focus", handleFocus, false);

    // Initialize with best-effort current state.
    set(typeof document.hasFocus === "function" ? document.hasFocus() : true);

    return () => {
      window.removeEventListener("blur", handleBlur, false);
      window.removeEventListener("focus", handleFocus, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { isFocused };
}

export default useWindowFocus;
